import type { ReactNode } from 'react';
import { legChainText } from '../../shared/summary_pill/leg_chain.js';
import type { FlightExplorerPageCopy } from './explorer_copy.js';
import { useExplorerEnv } from './explorer_env.js';
import {
  ScreenBar,
  ScreenBarCenter,
  ScreenBarEnd,
  ScreenBarRow,
  ScreenBarStart,
} from '@voll-intelligence/ui-kit/ui/screen-bar';
import type { ExplorerState } from './explorer_state.js';

/**
 * Título do chrome: a cadeia de praças + a janela de datas. No multi a cadeia
 * INTEIRA (`GRU → REC → SSA`), não o par ponta-a-ponta: com N trechos um
 * `origem → destino` mentiria sobre o roteiro. Um traslado por terra quebra a
 * cadeia (`GRU → REC · SSA → CNF`, `legChainText`).
 */
function explorerTitle(state: ExplorerState, page: FlightExplorerPageCopy): string {
  const { legs, mode, returnDate } = state.request;
  const first = legs[0];
  if (!first) return '';
  const chain = legChainText(legs);
  const depart = first.departDate;
  const end = mode === 'round_trip' ? returnDate : legs.at(-1)?.departDate;
  const when = page.tripDates(depart, end === depart ? undefined : end);
  return when ? `${chain} · ${when}` : chain;
}

/** O título da barra: a cadeia de praças + a janela de datas. É a única coisa
 *  que a barra mostra quando não há `pill` — o título DA LISTA (com o placar,
 *  no RT/multi) mora na coluna (`ExplorerResults`), não mais aqui. */
function TopBarTitle({ state }: Readonly<{ state: ExplorerState }>): ReactNode {
  const { copy } = useExplorerEnv();
  return <h3 className="fex-title">{explorerTitle(state, copy.page)}</h3>;
}

/** O que a casca TAKEOVER da conversa põe na barra e o explorer não conhece:
 *  a trilha do desktop e o slot da fila de chips do celular, a segunda linha da
 *  barra (#4003). A bancada não passa: lá a barra é a do shell. */
export type FlightExplorerTakeover = {
  readonly trail?: ReactNode;
  readonly setChipsSlot: (element: HTMLElement | null) => void;
};

/**
 * Chrome do explorer — a MESMA barra da bancada (padronização de 30/09,
 * #3875; a `ScreenBar` do ui-kit desde #4003): o ✕ do host à
 * esquerda, a pill de contexto (ou, sem ela, o título da cadeia de praças)
 * CENTRADA, e no canto DIREITO o `trailing` do host (#3301). A barra não pula
 * quando a etapa troca — é a mesma na lista e na tela de tarifas. Barra única (Task 5): o `→|` dos cortes, o total parcial e
 * o Confirmar morreram daqui — o corte mostra o painel sempre que há o que
 * cortar (sem toggle), e o fecho é o CTA da tela de tarifas. Quem decide se
 * esta barra existe é o `FlightExplorer` (`hasBar`): sem `chrome`, `pill` nem
 * `trailing`, ela não renderiza — `.fex--bare`.
 *
 * Fora de `explorer.tsx` porque ele vive no teto de linhas do workspace.
 */
export function ExplorerTopBar({
  chrome,
  pill,
  trailing,
  takeover,
  state,
}: {
  readonly chrome?: ReactNode;
  /** `bandAsPill`: a pill de contexto NO LUGAR do título estático — a mesma
   *  informação (forma · rota · datas), agora clicável. */
  readonly pill?: ReactNode;
  /** O canto direito é do host: no takeover do chat, a mala da viagem — o
   *  takeover cobre o top bar do shell, e sem este slot a pessoa perdia o
   *  itinerário justamente quando precisa dele (#3301). */
  readonly trailing?: ReactNode;
  readonly takeover?: FlightExplorerTakeover | undefined;
  readonly state: ExplorerState;
}) {
  const trail = takeover?.trail;
  const chipsSlot = takeover?.setChipsSlot;
  return (
    <ScreenBar className="fex-top">
      <ScreenBarStart>
        {chrome}
        {trail}
      </ScreenBarStart>
      <ScreenBarCenter>{pill ?? <TopBarTitle state={state} />}</ScreenBarCenter>
      <ScreenBarEnd>{trailing}</ScreenBarEnd>
      {chipsSlot && <ScreenBarRow ref={chipsSlot} />}
    </ScreenBar>
  );
}

/** A raiz só é barra (`.fex`) quando há chrome, pill OU trailing pra mostrar —
 *  sem os três, `fex--bare` (zera `--fex-chrome-h`) e nenhuma `ExplorerTopBar`
 *  monta. Extraída (mesmo padrão de `pillNodeFor`) pelo teto de complexidade do
 *  `FlightExplorer`. */
export function fexRootClass(
  chrome: ReactNode,
  pillNode: ReactNode,
  trailing: ReactNode,
): { hasBar: boolean; className: string } {
  // `??` deixaria um `chrome={false}` (ReactNode válido, mas falsy) mascarar uma
  // pill VIVA: `false ?? pillNode` nunca chega no `??` porque `false` não é
  // nullish, e a barra sumiria com uma pill em cena. Cada presença conta por si.
  const hasBar = Boolean(chrome) || Boolean(pillNode) || Boolean(trailing);
  return { hasBar, className: hasBar ? 'fex' : 'fex fex--bare' };
}
